import { useEffect, useMemo, useState } from 'react';
import {
  Link, useLocation, useHistory, matchPath, generatePath,
} from 'react-router-dom';
import classNames from 'classnames';

import {
  REDIRECT_SEARCH_PARAM,
  ROUTE_HOME,
  ROUTE_LOGIN,
  ROUTE_MEMBERSHIP,
  ROUTE_MY_ACCOUNT,
  ROUTE_ARTICLES,
  ROUTE_DASHBOARD_REWARDS,
} from '../../config/routes';
import trashieLogo from '../../images/trashie-logo.webp';
import { isMobile } from '../../../../hooks/useBreakPoint';
import {
  checkRouteWithCloseButton,
  getCurrentRouteParentRoute,
  getCurrentRouteTitle,
  getNavigationItems,
  getCurrentRouteHeaderBackground,
  getArticleLoginRedirect,
  prepareArticleLoginRedirect,
} from '../../utils/routes';
import ACCOUNT_DRAWER_OPEN_EVENT, {
  ACCOUNT_DRAWER_STATE_EVENT,
  REWARDS_SEARCH_OPEN_EVENT,
  HEADER_BACK_INTERCEPT_EVENT,
  HEADER_BACK_VISIBILITY_EVENT,
} from '../../config/events';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import HeaderAccount from './HeaderAccount';
import BuyTbbButton from '../BuyTbbButton/BuyTbbButton';
import NavigationBar from '../NavigationBar';
import TCNavPill from '../TCNavPill/TCNavPill';
import ArrowLeftIcon from '../../images/arrow-left-icon.svg';
import SearchSvg from '../SvgComponents/Search_svg';
import { getAccountTabInitial } from '../navigationColors';
import useUserBalance from '../../utils/hooks/useUserBalance';

import styles from './Header.module.scss';

const {
  main,
  container,
  leftCluster,
  rightCluster,
  logo,
  img,
  divider,
  ctaContainer,
  cta,
  ctaActive,
  ctaIcon,
  ctaLabel,
  searchPill,
  searchPillText,
  tcPillLeft,
  accountInitialCircle,
  accountInitialCircleActive,
} = styles;

const SEARCH_CONFIG = [
  { route: ROUTE_DASHBOARD_REWARDS, event: REWARDS_SEARCH_OPEN_EVENT, label: 'Search rewards' },
];

const Header = ({ isBannerVisible }) => {
  const {
    isSignedIn, userData, user, loading,
  } = useFirebaseAuth();
  const balance = useUserBalance(isSignedIn, userData);

  const location = useLocation();
  const articleLoginRedirect = getArticleLoginRedirect(location);
  const history = useHistory();
  const mobile = isMobile();
  const isMobileMyAccount = mobile && location.pathname === ROUTE_MY_ACCOUNT;
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);
  const [isBackHidden, setIsBackHidden] = useState(false);

  const {
    parentRoute,
    withCloseButton,
    headerBackground,
    routeTitle,
  } = useMemo(() => ({
    parentRoute: getCurrentRouteParentRoute(),
    withCloseButton: checkRouteWithCloseButton(),
    headerBackground: getCurrentRouteHeaderBackground(),
    routeTitle: getCurrentRouteTitle(),
  }), [location.pathname]);

  const activeSearch = SEARCH_CONFIG.find((s) => s.route === location.pathname);
  const showHeaderSearch = !mobile && isSignedIn && userData && !!activeSearch;

  const className = classNames(main, {
    shadow: !isBannerVisible && !mobile,
    [styles.mainParentRoute]: mobile && parentRoute,
  });

  const goBack = () => {
    // Let pages with in-route sub-flows intercept back before we navigate away.
    const interceptEvent = new Event(HEADER_BACK_INTERCEPT_EVENT, { cancelable: true });
    window.dispatchEvent(interceptEvent);
    if (interceptEvent.defaultPrevented) {
      return;
    }

    // Membership hub sub-pages are nested under /account in route metadata, so parentRoute
    // would open the drawer; users expect back to go to /membership instead.
    if (location.pathname.startsWith(`${ROUTE_MEMBERSHIP}/`)) {
      history.push(ROUTE_MEMBERSHIP);
      return;
    }

    if (parentRoute === ROUTE_MY_ACCOUNT) {
      setIsAccountDrawerOpen(true);
      window.dispatchEvent(new Event(ACCOUNT_DRAWER_OPEN_EVENT));
      return;
    }

    if (parentRoute) {
      let target = parentRoute;
      if (parentRoute.includes(':')) {
        const match = matchPath(location.pathname, { path: parentRoute });
        if (match && match.params) {
          try {
            target = generatePath(parentRoute, match.params);
          } catch {
            target = parentRoute;
          }
        }
      }
      history.push(target);
    }
  };

  const handleSearchClick = () => {
    if (activeSearch) {
      window.dispatchEvent(new Event(activeSearch.event));
    }
  };

  useEffect(() => {
    const handleDrawerState = (event) => {
      setIsAccountDrawerOpen(Boolean(event?.detail?.isOpen));
    };

    window.addEventListener(ACCOUNT_DRAWER_STATE_EVENT, handleDrawerState);
    return () => window.removeEventListener(ACCOUNT_DRAWER_STATE_EVENT, handleDrawerState);
  }, []);

  useEffect(() => {
    const handleBackVisibility = (event) => {
      setIsBackHidden(Boolean(event?.detail?.hidden));
    };

    window.addEventListener(HEADER_BACK_VISIBILITY_EVENT, handleBackVisibility);
    return () => window.removeEventListener(HEADER_BACK_VISIBILITY_EVENT, handleBackVisibility);
  }, []);

  // Route change resets the hidden flag — next page starts with default chevron behaviour.
  useEffect(() => {
    setIsBackHidden(false);
  }, [location.pathname]);

  if (isMobileMyAccount) {
    return null;
  }

  if (mobile) {
    return (
      <nav className={className} style={{ background: headerBackground }}>
        <div className={container}>
          {!parentRoute && !(isSignedIn && userData) && (
            <Link className={logo} to={ROUTE_HOME}>
              <img className={img} src={trashieLogo} alt="trashie logo" />
            </Link>
          )}
          {isSignedIn && userData && !parentRoute && (
            <div className={tcPillLeft}>
              <TCNavPill
                balance={balance !== null ? balance : userData.balance?.currentBalance}
              />
            </div>
          )}
          {parentRoute && isSignedIn && userData && !withCloseButton ? (
            <div className={styles.parentNav}>
              <div className={styles.parentNavSlotStart}>
                {!isBackHidden && (
                  <button
                    type="button"
                    className={styles.parentNavBack}
                    onClick={goBack}
                    aria-label="Back"
                  >
                    <img src={ArrowLeftIcon} alt="" />
                  </button>
                )}
              </div>
              <div className={styles.parentNavSlotCenter}>
                <span className={styles.parentNavTitle}>{routeTitle}</span>
              </div>
              <div className={styles.parentNavSlotEnd}>
                <TCNavPill
                  balance={balance !== null ? balance : userData.balance?.currentBalance}
                />
              </div>
            </div>
          ) : parentRoute && (
            <NavigationBar
              title={routeTitle}
              onGoBack={isBackHidden || withCloseButton ? undefined : goBack}
              onClose={!isBackHidden && withCloseButton ? goBack : undefined}
            />
          )}
          {isSignedIn && userData && !parentRoute && (
            <HeaderAccount />
          )}
          {!isSignedIn && !loading && !parentRoute && (
            <BuyTbbButton />
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav className={className}>
      <div className={container}>
        <div className={leftCluster}>
          <Link className={logo} to={ROUTE_HOME}>
            <img className={img} src={trashieLogo} alt="trashie logo" />
          </Link>
          <span className={divider} aria-hidden="true" />
          <div className={ctaContainer}>
            {getNavigationItems().map(({ route, label, icon: Icon }) => {
              const isArticlesTab = route === ROUTE_ARTICLES
                && location.pathname.startsWith(`${ROUTE_ARTICLES}/`);
              const isActive = isAccountDrawerOpen
                ? route === ROUTE_MY_ACCOUNT
                : location.pathname === route || isArticlesTab;

              const isAuthed = isSignedIn && userData;
              const linkTarget = (() => {
                if (route === ROUTE_MY_ACCOUNT && !isAuthed) {
                  return articleLoginRedirect ? {
                    pathname: ROUTE_LOGIN,
                    search: new URLSearchParams({
                      [REDIRECT_SEARCH_PARAM]: articleLoginRedirect,
                    }).toString(),
                  } : ROUTE_LOGIN;
                }
                return location.pathname === route ? location : route;
              })();
              return (
                <Link
                  className={classNames(cta, { [ctaActive]: isActive })}
                  to={linkTarget}
                  key={`headerItem${label}`}
                  onClick={(event) => {
                    if (route !== ROUTE_MY_ACCOUNT) {
                      return;
                    }
                    // Unauth: let the Link navigate to /login. Drawer is auth-only.
                    if (!isAuthed) {
                      prepareArticleLoginRedirect(location);
                      return;
                    }

                    event.preventDefault();
                    setIsAccountDrawerOpen(true);
                    window.dispatchEvent(new Event(ACCOUNT_DRAWER_OPEN_EVENT));
                  }}
                >
                  <span className={ctaIcon}>
                    {(() => {
                      const initial = route === ROUTE_MY_ACCOUNT
                        ? getAccountTabInitial(user, userData)
                        : '';
                      return initial ? (
                        <span
                          className={classNames(
                            accountInitialCircle,
                            { [accountInitialCircleActive]: isActive },
                          )}
                        >
                          {initial}
                        </span>
                      ) : (
                        <Icon
                          height={24}
                          width={24}
                          color={isActive
                            ? 'var(--membership-ice-pink)'
                            : 'var(--trashie-neutral-700)'}
                        />
                      );
                    })()}
                  </span>
                  <span className={ctaLabel}>{label}</span>
                </Link>
              );
            })}
          </div>
        </div>
        {isSignedIn && userData && (
          <div className={rightCluster}>
            {showHeaderSearch && (
              <button
                type="button"
                className={searchPill}
                onClick={handleSearchClick}
                aria-label={activeSearch?.label || 'Search'}
              >
                <SearchSvg width={16} height={16} color="var(--trashie-neutral-300)" />
                <span className={searchPillText}>{activeSearch?.label || 'Search'}</span>
              </button>
            )}
            <HeaderAccount />
          </div>
        )}
        {!isSignedIn && !loading && (
          <div className={rightCluster}>
            <BuyTbbButton />
          </div>
        )}
      </div>
    </nav>
  );
};

export default Header;
